import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import React, { useEffect } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { getFirebaseApp } from '@/libs/firebase/init.web';
import { captureException } from '@/libs/logger';
import { useOidcInteractionStore } from '@/stores';
import { colors } from '@/theme/colors';

// postMessage protocol shared with the SSO interact page (usk-auth).
const READY = 'usk-silent-login-ready';
const TOKEN = 'usk-silent-login-token';
const RESULT = 'usk-silent-login-result';

/**
 * Single-shot guard at MODULE scope. `signInWithCustomToken` flips the app to authenticated, which
 * can remount this screen; a per-instance flag wouldn't survive that, so a late/duplicate message
 * could sign in twice. Keyed as a boolean (unlike shop-bridge's per-code key) since there is only
 * ever one token per iframe. Reset on failure so a retry isn't permanently blocked.
 */
let handled = false;

/**
 * Accept a message only from a page on the SAME registrable domain as this app — the SSO interact
 * page is a sibling `*.uskidsgolf.com` origin. We deliberately do NOT key off the OIDC issuer
 * (`ssoIssuerUrl`), which is a different backend domain than the interact FE, and we don't hardcode
 * the interact subdomain (it varies per env). The token only re-establishes the sender's OWN
 * identity, and browser storage partitioning already means a cross-site parent could never seed
 * this origin — so "same registrable domain, over https" is the correct trust boundary.
 */
const isSameSiteParent = (origin: string): boolean => {
  try {
    const url = new URL(origin);
    if (url.protocol !== 'https:') return false;
    // Registrable domain for our `*.com` hosts = last two labels (e.g. `uskidsgolf.com`).
    const base = window.location.hostname.split('.').slice(-2).join('.');
    return url.hostname === base || url.hostname.endsWith(`.${base}`);
  } catch {
    return false;
  }
};

/**
 * Silent cross-subdomain auto-login target (web-only, guardian-view-be docs/tasks/202610131805).
 * Rendered inside a hidden iframe the SSO interact page embeds after a successful login. We announce
 * readiness to the parent, then accept a short-lived Firebase custom token over postMessage — ONLY
 * from a sibling same-site `*.uskidsgolf.com` origin — and sign the guardian web session in with it,
 * so the user is already authenticated when they open the app first-party.
 *
 * We only ever ACCEPT a token here (never emit data) and verify the sender origin on every message.
 * The OIDC-interaction gate is flagged active so `withUnAuth` keeps this screen mounted when the
 * sign-in flips Firebase to authenticated, instead of resetting to the dashboard.
 */
export function SilentLoginScreen() {
  const setActive = useOidcInteractionStore(state => state.setActive);

  useEffect(() => {
    setActive(true);
    return () => setActive(false);
  }, [setActive]);

  useEffect(() => {
    // Only meaningful inside an iframe; a top-level visit just shows the spinner and does nothing.
    if (typeof window === 'undefined' || window.parent === window) return;

    const onMessage = (event: MessageEvent) => {
      if (!isSameSiteParent(event.origin)) return;
      const data = event.data as { type?: unknown; customToken?: unknown } | null;
      if (!data || typeof data !== 'object' || data.type !== TOKEN) return;
      if (handled || typeof data.customToken !== 'string') return;
      handled = true;
      const customToken = data.customToken;
      // Reply to the exact verified sender (the interact page), not a broadcast.
      const parentOrigin = event.origin;
      (async () => {
        try {
          const auth = getAuth(getFirebaseApp());
          // Sign in as the SSO account, replacing any stale session already on this origin.
          await signOut(auth).catch(() => undefined);
          await signInWithCustomToken(auth, customToken);
          window.parent.postMessage({ type: RESULT, ok: true }, parentOrigin);
        } catch (error) {
          handled = false;
          captureException(
            { error_payload: error },
            { tags: { usk_log_type: 'silent_login_error' } },
          );
          window.parent.postMessage({ type: RESULT, ok: false }, parentOrigin);
        }
      })();
    };

    window.addEventListener('message', onMessage);
    // Announce readiness so the parent posts the token. The parent's exact origin isn't known here
    // (it varies per env), so this contentless ping uses '*'; the token itself is gated by
    // isSameSiteParent on receipt, and the parent scopes the token to our origin.
    window.parent.postMessage({ type: READY }, '*');
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <View style={styles.center}>
      <ActivityIndicator size="large" color={colors['10']} />
    </View>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});

export default SilentLoginScreen;
